import {
  Autocomplete,
  Button,
  Card,
  FormControl,
  FormHelperText,
  IconButton,
  SvgIcon,
  TextField,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { RiDeleteBinLine } from '@remixicon/react';
import { useMutation } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import CustomChip from '../../../../components/CustomChip';
import FileUploader from '../../../../components/FileUploader';
import InputHeader from '../../../../components/InputHeader';
import RejectionNotesTimeline from '../../../../components/RejectionNotesTimeline';
import { useAppDataContext } from '../../../../contexts/appData';
import { useCurrentUser } from '../../../../contexts/currentUser';
import { useIotsApi } from '../../../../contexts/iotsApi';
import { useLanguage } from '../../../../contexts/language';
import useForm from '../../../../hooks/useForm';
import { useNavigationGuardBlocker } from '../../../../hooks/useNavigationGuard';
import { AppFile, VesselCertificateStatus } from '../../../../types/core';
import { Role } from '../../../../types/user';
import {
  NewVesselCertificate,
  Vessel,
  VesselCertificate,
} from '../../../../types/vessel';
import {
  DAYJS_DATA_DATE_FORMAT,
  DAYJS_VISUALIZATION_DATE_FORMAT,
  getNullableDayjsDifferenceChecker,
} from '../../../../utils/date';
import VesselCertificateStatusBadge from '../VesselCertificateStatusBadge';
import VesselRejectionDialog from '../VesselRejectionDialog';
import { validateNumber } from '../../../../utils/validation';
import useApiErrorMessageGetter from '../../../../hooks/useApiErrorMessage';

export interface CertificationCardElementProps {
  vessel: Vessel;
  certificate: VesselCertificate | null;
  isLoading: boolean;
  onDelete: (hasPendingChanges: boolean) => void;
  onSave: (certificateValues: NewVesselCertificate) => void;
  onBaseDataUpdate?: (data: VesselCertificate) => void;
}

type CertificationFormValues = {
  classMode: string;
  minimumCrew: string;
  maximumPassengers: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  files: AppFile[];
};

const CertificationCard = ({
  vessel,
  certificate,
  isLoading,
  onDelete,
  onSave,
  onBaseDataUpdate,
}: CertificationCardElementProps) => {
  const { t } = useLanguage();
  const { isOperator, hasRole } = useCurrentUser();
  const { appData } = useAppDataContext();
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const { approveVesselCertificate } = useIotsApi();

  const [displayingRejectionDialog, setDisplayingRejectionDialog] =
    useState(false);

  const isAdmin = hasRole(Role.ADMIN);

  const onSuccessfulApproveReject = (certificate: VesselCertificate) => {
    onBaseDataUpdate?.(certificate);
  };

  const initialValues = useMemo<CertificationFormValues>(() => {
    if (!certificate) {
      return {
        classMode: '',
        minimumCrew: '',
        maximumPassengers: '',
        startDate: null,
        endDate: null,
        files: [],
      };
    }
    return {
      classMode: certificate.classMode,
      minimumCrew: certificate.minimumCrew.toString(),
      maximumPassengers: certificate.maximumPassengers.toString(),
      startDate: dayjs(certificate.startDate),
      endDate: dayjs(certificate.endDate),
      files: certificate.files,
    };
  }, [certificate]);

  const today = dayjs(new Date());

  const approveVesselCertificateMutation = useMutation({
    mutationFn: (certificateId: string) =>
      approveVesselCertificate(vessel.id, certificateId),
    onSuccess: certificate => {
      onSuccessfulApproveReject(certificate);
      enqueueSnackbar({
        variant: 'success',
        message: t('content.certificate-approval-success'),
      });
    },
    onError: error => {
      enqueueSnackbar({
        variant: 'error',
        message: getErrorMessage(
          error,
          t('content.certificate-approval-error'),
        ),
      });
      approveVesselCertificateMutation.reset();
    },
  });

  const {
    values,
    errors,
    updateValues,
    touchField,
    haveValuesChanged,
    handleSubmit,
  } = useForm({
    initialValues,
    onSubmit: values => {
      onSave({
        classMode: values.classMode,
        minimumCrew: Number(values.minimumCrew),
        maximumPassengers: Number(values.maximumPassengers),
        startDate: values.startDate
          ? values.startDate.format(DAYJS_DATA_DATE_FORMAT)
          : '',
        endDate: values.endDate
          ? values.endDate.format(DAYJS_DATA_DATE_FORMAT)
          : '',
        files: values.files,
      });
    },
    validate: (values, setError) => {
      if (!values.classMode) {
        setError('classMode', t('validation.required-field'));
      }

      const minimumCrewValidation = validateNumber(values.minimumCrew);
      if (!minimumCrewValidation.isNumber) {
        if (!minimumCrewValidation.isEmpty) {
          setError('minimumCrew', t('validation.required-field'));
        } else {
          setError('minimumCrew', t('validation.must-be-numeric'));
        }
      } else if (
        minimumCrewValidation.isZero ||
        minimumCrewValidation.isNegative
      ) {
        setError(
          'minimumCrew',
          t(`validation.must-be-number-greater-than-reference`, {
            REFERENCE: '0',
          }),
        );
      } else if (!minimumCrewValidation.isInteger) {
        setError('minimumCrew', t('validation.must-be-numeric-integer'));
      }

      const maximumPassengersValidation = validateNumber(
        values.maximumPassengers,
      );
      if (!maximumPassengersValidation.isNumber) {
        if (!maximumPassengersValidation.isEmpty) {
          setError('maximumPassengers', t('validation.required-field'));
        } else {
          setError('maximumPassengers', t('validation.must-be-numeric'));
        }
      } else if (!maximumPassengersValidation.isInteger) {
        setError('maximumPassengers', t('validation.must-be-numeric-integer'));
      } else if (maximumPassengersValidation.isNegative) {
        setError(
          'maximumPassengers',
          t(`validation.must-be-number-greater-than-or-equal-to-reference`, {
            REFERENCE: '0',
          }),
        );
      }

      if (!values.startDate) {
        setError('startDate', t('validation.required-field'));
      } else if (!values.startDate.isValid()) {
        setError('startDate', t('validation.invalid-date'));
      } else if (values.startDate.isAfter(today, 'day')) {
        setError(
          'startDate',
          t('validation.date-after-reference-date', {
            REFERENCE_DATE: today.format(DAYJS_VISUALIZATION_DATE_FORMAT),
          }),
        );
      }
      if (!values.endDate) {
        setError('endDate', t('validation.required-field'));
      } else if (!values.endDate.isValid()) {
        setError('endDate', t('validation.invalid-date'));
      } else if (
        values.startDate &&
        (values.startDate.isSame(values.endDate, 'day') ||
          values.startDate.isAfter(values.endDate, 'day'))
      ) {
        setError('endDate', t('validation.invalid-end-date'));
      }
      if (!values.files.length) {
        setError('files', t('validation.required-file'));
      }
    },
    differenceChecker: {
      startDate: getNullableDayjsDifferenceChecker('day'),
      endDate: getNullableDayjsDifferenceChecker('day'),
    },
    resetOnInitialValuesChange: () => !!certificate,
  });

  useNavigationGuardBlocker(() => haveValuesChanged);

  // TODO: Enable edition
  const areInputsDisabled = isLoading || Boolean(certificate);

  const shouldShowDeleteButton =
    !certificate ||
    isAdmin ||
    (!isAdmin && certificate.status === VesselCertificateStatus.Rejected);

  const shouldShowApproveRejectButtons =
    certificate?.status === VesselCertificateStatus.PendingApproval &&
    !isOperator;

  const sortedClasModeOptions = useMemo(() => {
    const opts = [...(appData?.vesselCertificateTypes ?? [])];
    return opts.sort((a, b) => {
      const labelA = t(`vessel-certificate-types.${a}`);
      const labelB = t(`vessel-certificate-types.${b}`);
      if (labelA < labelB) return -1;
      if (labelA > labelB) return 1;
      return 0;
    });
  }, [appData?.vesselCertificateTypes, t]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card variant="outlined">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <header className="col-span-full font-bold flex gap-4">
            <span>{t('content.certification')}</span>
            <>
              {certificate ? (
                <VesselCertificateStatusBadge
                  status={certificate?.status}
                  isExpired={certificate?.isExpired}
                />
              ) : (
                <CustomChip small label={t('content.draft')} />
              )}
              {shouldShowDeleteButton && (
                <IconButton
                  disabled={isLoading}
                  onClick={() => onDelete(haveValuesChanged)}
                  className="ml-auto"
                >
                  <SvgIcon component={RiDeleteBinLine} />
                </IconButton>
              )}
            </>
          </header>
          <div className="flex flex-col lg:flex-row gap-4 justify-between">
            <label className="block w-full">
              <InputHeader required>{t('content.class-mode')}</InputHeader>

              <FormControl error={Boolean(errors.classMode)}>
                <Autocomplete
                  value={values.classMode}
                  onChange={(_event, selectedOption) =>
                    updateValues({ classMode: selectedOption || '' }, true)
                  }
                  options={sortedClasModeOptions}
                  onBlur={() => touchField('classMode')}
                  disabled={areInputsDisabled}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={Boolean(errors.classMode)}
                      placeholder={t('content.select')}
                    />
                  )}
                  getOptionLabel={classMode =>
                    classMode ? t(`vessel-certificate-types.${classMode}`) : ''
                  }
                />
                {errors.classMode && (
                  <FormHelperText>{errors.classMode}</FormHelperText>
                )}
              </FormControl>
            </label>
            <label className="block w-full">
              <InputHeader
                required
                helperText={t('content.minimum-crew-tootlip')}
              >
                {t('content.minimum-crew')}
              </InputHeader>
              <TextField
                placeholder={t('content.minimum-crew')}
                name="minimumCrew"
                value={values.minimumCrew}
                onChange={event =>
                  updateValues({ minimumCrew: event.target.value })
                }
                onBlur={() => touchField('minimumCrew')}
                error={Boolean(errors.minimumCrew)}
                helperText={errors.minimumCrew}
                disabled={areInputsDisabled}
              />
            </label>
            <label className="block w-full">
              <InputHeader required>
                {t('content.maximum-passengers')}
              </InputHeader>
              <TextField
                placeholder={t('content.maximum-passengers')}
                name="maximumPassengers"
                value={values.maximumPassengers}
                onChange={event =>
                  updateValues({ maximumPassengers: event.target.value })
                }
                onBlur={() => touchField('maximumPassengers')}
                error={Boolean(errors.maximumPassengers)}
                helperText={errors.maximumPassengers}
                disabled={areInputsDisabled}
              />
            </label>
          </div>
          <div className="flex flex-col lg:flex-row gap-4">
            <label className="block w-full">
              <InputHeader required>{t('content.start-date')}</InputHeader>
              <FormControl error={Boolean(errors.startDate)}>
                <DatePicker
                  className="w-full"
                  format={DAYJS_VISUALIZATION_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.startDate),
                      onBlur: () => touchField('startDate'),
                      disabled: areInputsDisabled,
                    },
                  }}
                  value={values.startDate}
                  maxDate={
                    values.endDate && values.endDate.isBefore(today)
                      ? values.endDate
                      : today
                  }
                  onChange={newValue =>
                    updateValues({
                      startDate: newValue,
                    })
                  }
                  disabled={areInputsDisabled}
                />
                {errors.startDate && (
                  <FormHelperText>{errors.startDate}</FormHelperText>
                )}
              </FormControl>
            </label>
            <label className="block w-full">
              <InputHeader required>{t('content.end-date')}</InputHeader>
              <FormControl error={Boolean(errors.endDate)}>
                <DatePicker
                  className="w-full"
                  format={DAYJS_VISUALIZATION_DATE_FORMAT}
                  slotProps={{
                    textField: {
                      error: Boolean(errors.endDate),
                      onBlur: () => touchField('endDate'),
                    },
                  }}
                  value={values.endDate}
                  minDate={values.startDate ?? today}
                  onChange={newValue =>
                    updateValues({
                      endDate: newValue,
                    })
                  }
                  disabled={areInputsDisabled}
                />
                {errors.endDate && (
                  <FormHelperText>{errors.endDate}</FormHelperText>
                )}
              </FormControl>
            </label>
          </div>
          <div>
            <InputHeader required>{t('content.files')}</InputHeader>
            <span className="text-iots-grey-500 text-sm">
              {t('content.files-accepted-formats')}
            </span>
            <FormControl error={Boolean(errors.files)}>
              <FileUploader
                isError={Boolean(errors.files)}
                onChange={(validFiles: AppFile[]) => {
                  updateValues({
                    files: [...values.files, ...validFiles],
                  });
                }}
                onDelete={(id: string) => {
                  updateValues({
                    files: values.files.filter(file => file.id !== id),
                  });
                }}
                files={values.files}
                disabled={areInputsDisabled}
              />
              {errors.files && <FormHelperText>{errors.files}</FormHelperText>}
            </FormControl>
          </div>
          <div>
            {certificate?.notes && (
              <RejectionNotesTimeline rejectionNotes={certificate.notes} />
            )}
          </div>
          <div>
            {shouldShowApproveRejectButtons && (
              <div className="flex gap-4 justify-end">
                <Button
                  type="button"
                  variant="outlined"
                  color="error"
                  onClick={() => setDisplayingRejectionDialog(true)}
                  disabled={isLoading}
                >
                  {t('content.reject')}
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() =>
                    approveVesselCertificateMutation.mutate(certificate.id)
                  }
                  loading={isLoading}
                  disabled={isLoading}
                >
                  {t('content.approve')}
                </Button>
                <VesselRejectionDialog
                  vesselId={displayingRejectionDialog ? vessel.id : null}
                  certificateId={certificate.id}
                  onClose={() => setDisplayingRejectionDialog(false)}
                  onConfirm={rejectedCertificate => {
                    setDisplayingRejectionDialog(false);
                    onSuccessfulApproveReject(rejectedCertificate);
                  }}
                />
              </div>
            )}
          </div>
          {!shouldShowApproveRejectButtons && (
            <div className="flex justify-end">
              <Button
                variant="contained"
                type="submit"
                disabled={
                  !haveValuesChanged ||
                  areInputsDisabled ||
                  Object.values(errors).length > 0
                }
              >
                {t('content.save')}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </LocalizationProvider>
  );
};

export default CertificationCard;
